import React, { useState, useEffect } from 'react';
import { 
  uploadDashboardData, 
  downloadDashboardData, 
  validateDashboardData,
  listBackups,
  getDashboardData,
  clearDataCache
} from '../services/dataService';
import authService from '../services/authService';
import Analytics from './Analytics';
import './AdminPanel.css';

const AdminPanel = ({ onBackToDashboard, onDataUpdated }) => {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [currentData, setCurrentData] = useState(null);
  const [backups, setBackups] = useState([]);
  const [validation, setValidation] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [activeTab, setActiveTab] = useState('data-management'); // 'data-management', 'analytics', or 'requests'
  const [solutionRequests, setSolutionRequests] = useState([]);

  useEffect(() => {
    loadCurrentData();
    loadBackups();
    loadSolutionRequests();
  }, []);

  const loadCurrentData = async () => {
    try {
      const data = await getDashboardData(true);
      setCurrentData(data);
    } catch (error) {
      showMessage('error', 'Failed to load current data');
    }
  };

  const loadBackups = async () => {
    try {
      const backupList = await listBackups();
      setBackups(backupList);
    } catch (error) {
      console.error('Failed to load backups:', error);
    }
  };

  const loadSolutionRequests = async () => {
    try {
      const response = await fetch('/api/admin/solution-requests/requests', {
        headers: authService.getAuthHeaders()
      });
      const result = await response.json();
      if (result.success) {
        setSolutionRequests(result.requests);
      }
    } catch (error) {
      console.error('Failed to load solution requests:', error);
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const updateRequestStatus = async (requestId, newStatus) => {
    try {
      const response = await fetch(`/api/admin/solution-requests/requests/${requestId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...authService.getAuthHeaders()
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        await loadSolutionRequests();
        showMessage('success', `Request status updated to ${newStatus}`);
      }
    } catch (error) {
      console.error('Failed to update request status:', error);
      showMessage('error', 'Failed to update request status');
    }
  };

  const editRequestNotes = async (request) => {
    const notes = prompt('Enter admin notes for this request:', request.notes || '');
    if (notes !== null) {
      try {
        const response = await fetch(`/api/admin/solution-requests/requests/${request.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            ...authService.getAuthHeaders()
          },
          body: JSON.stringify({ notes })
        });

        if (response.ok) {
          await loadSolutionRequests();
          showMessage('success', 'Notes updated successfully');
        }
      } catch (error) {
        console.error('Failed to update notes:', error);
        showMessage('error', 'Failed to update notes');
      }
    }
  };

  const deleteRequest = async (requestId) => {
    if (confirm('Are you sure you want to delete this request? This action cannot be undone.')) {
      try {
        const response = await fetch(`/api/admin/solution-requests/requests/${requestId}`, {
          method: 'DELETE',
          headers: authService.getAuthHeaders()
        });

        if (response.ok) {
          await loadSolutionRequests();
          showMessage('success', 'Request deleted successfully');
        }
      } catch (error) {
        console.error('Failed to delete request:', error);
        showMessage('error', 'Failed to delete request');
      }
    }
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'low': return '🟢';
      case 'medium': return '🟡';
      case 'high': return '🟠';
      case 'urgent': return '🔴';
      default: return '🟡';
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.json')) {
      showMessage('error', 'Please select a JSON file');
      return;
    }

    setLoading(true);
    setUploadProgress(0);

    try {
      // Simulate progress
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 10, 90));
      }, 100);

      const result = await uploadDashboardData(file);
      
      clearInterval(progressInterval);
      setUploadProgress(100);

      showMessage('success', `Data uploaded successfully! Backup created: ${result.backup}`);
      
      // Refresh data and notify parent
      await loadCurrentData();
      await loadBackups();
      
      if (onDataUpdated) {
        onDataUpdated();
      }

    } catch (error) {
      showMessage('error', `Upload failed: ${error.message}`);
    } finally {
      setLoading(false);
      setUploadProgress(0);
      // Clear file input
      event.target.value = '';
    }
  };

  const handleDownload = async () => {
    try {
      setLoading(true);
      await downloadDashboardData();
      showMessage('success', 'Data downloaded successfully');
    } catch (error) {
      showMessage('error', `Download failed: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleValidateFile = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.json')) {
      showMessage('error', 'Please select a JSON file');
      return;
    }

    setLoading(true);

    try {
      const fileContent = await file.text();
      const data = JSON.parse(fileContent);
      
      const result = await validateDashboardData(data);
      setValidation(result.validation);
      
      if (result.validation.valid) {
        showMessage('success', 'File validation passed!');
      } else {
        showMessage('warning', `File has ${result.validation.errors.length} errors`);
      }

    } catch (error) {
      showMessage('error', `Validation failed: ${error.message}`);
      setValidation(null);
    } finally {
      setLoading(false);
      event.target.value = '';
    }
  };

  const handleRefreshData = async () => {
    setLoading(true);
    try {
      clearDataCache();
      await loadCurrentData();
      showMessage('success', 'Data refreshed successfully');
      
      if (onDataUpdated) {
        onDataUpdated();
      }
    } catch (error) {
      showMessage('error', 'Failed to refresh data');
    } finally {
      setLoading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (isoString) => {
    return new Date(isoString).toLocaleString();
  };

  return (
    <div className="admin-panel">
      <header className="admin-header">
        <div className="admin-header-content">
          <div>
            <h1>🔧 Admin Panel</h1>
            <p>Manage dashboard data and configuration</p>
          </div>
          <button 
            onClick={onBackToDashboard}
            className="btn-back"
          >
            ← Back to Dashboard
          </button>
        </div>
      </header>

      {message.text && (
        <div className={`message message-${message.type}`}>
          {message.text}
        </div>
      )}

      {/* Tab Navigation */}
      <div className="admin-tabs">
        <button 
          className={`tab-button ${activeTab === 'data-management' ? 'active' : ''}`}
          onClick={() => setActiveTab('data-management')}
        >
          📊 Data Management
        </button>
        <button 
          className={`tab-button ${activeTab === 'analytics' ? 'active' : ''}`}
          onClick={() => setActiveTab('analytics')}
        >
          📈 Analytics
        </button>
        <button 
          className={`tab-button ${activeTab === 'requests' ? 'active' : ''}`}
          onClick={() => setActiveTab('requests')}
        >
          🚀 Solution Requests
          {solutionRequests.filter(req => req.status === 'pending').length > 0 && (
            <span className="tab-badge">
              {solutionRequests.filter(req => req.status === 'pending').length}
            </span>
          )}
        </button>
      </div>

      <div className="admin-content">
        {/* Data Management Tab */}
        {activeTab === 'data-management' && (
          <>
            {/* Current Data Status */}
            <section className="admin-section">
          <h2>📊 Current Data Status</h2>
          <div className="data-status-grid">
            <div className="status-card">
              <h3>Applications</h3>
              <div className="status-value">{currentData?.apps?.length || 0}</div>
            </div>
            <div className="status-card">
              <h3>Categories</h3>
              <div className="status-value">{currentData?.categories?.length || 0}</div>
            </div>
            <div className="status-card">
              <h3>Featured Apps</h3>
              <div className="status-value">
                {currentData?.apps?.filter(app => app.featured)?.length || 0}
              </div>
            </div>
            <div className="status-card">
              <h3>Last Updated</h3>
              <div className="status-date">
                {currentData?.metadata?.lastUpdated ? 
                  formatDate(currentData.metadata.lastUpdated) : 'Unknown'}
              </div>
            </div>
          </div>
          
          <div className="admin-actions">
            <button 
              onClick={handleRefreshData}
              disabled={loading}
              className="btn-refresh"
            >
              🔄 Refresh Data
            </button>
          </div>
        </section>

        {/* File Upload */}
        <section className="admin-section">
          <h2>📤 Upload New Data</h2>
          <div className="upload-area">
            <input
              type="file"
              accept=".json"
              onChange={handleFileUpload}
              disabled={loading}
              id="file-upload"
              className="file-input"
            />
            <label htmlFor="file-upload" className="file-label">
              <div className="upload-content">
                <div className="upload-icon">📁</div>
                <div className="upload-text">
                  <div>Click to select JSON file</div>
                  <div className="upload-subtext">Maximum file size: 10MB</div>
                </div>
              </div>
            </label>
            
            {uploadProgress > 0 && (
              <div className="upload-progress">
                <div 
                  className="progress-bar" 
                  style={{ width: `${uploadProgress}%` }}
                ></div>
                <span className="progress-text">{uploadProgress}%</span>
              </div>
            )}
          </div>

          <div className="upload-notes">
            <h4>📋 Upload Notes:</h4>
            <ul>
              <li>File must be in valid JSON format</li>
              <li>Current data will be automatically backed up</li>
              <li>Changes take effect immediately</li>
              <li>Dashboard will refresh automatically</li>
            </ul>
          </div>
        </section>

        {/* File Validation */}
        <section className="admin-section">
          <h2>✅ Validate Data File</h2>
          <div className="validate-area">
            <input
              type="file"
              accept=".json"
              onChange={handleValidateFile}
              disabled={loading}
              id="file-validate"
              className="file-input"
            />
            <label htmlFor="file-validate" className="file-label validate-label">
              <div className="upload-content">
                <div className="upload-icon">🔍</div>
                <div className="upload-text">
                  <div>Validate JSON file</div>
                  <div className="upload-subtext">Check file before uploading</div>
                </div>
              </div>
            </label>
          </div>

          {validation && (
            <div className="validation-results">
              <h4>Validation Results:</h4>
              <div className={`validation-status ${validation.valid ? 'valid' : 'invalid'}`}>
                {validation.valid ? '✅ Valid' : '❌ Invalid'}
              </div>
              
              {validation.stats && (
                <div className="validation-stats">
                  <div>Apps: {validation.stats.appsCount}</div>
                  <div>Categories: {validation.stats.categoriesCount}</div>
                  <div>Featured: {validation.stats.featuredAppsCount}</div>
                </div>
              )}
              
              {validation.errors?.length > 0 && (
                <div className="validation-errors">
                  <h5>Errors:</h5>
                  <ul>
                    {validation.errors.map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              {validation.warnings?.length > 0 && (
                <div className="validation-warnings">
                  <h5>Warnings:</h5>
                  <ul>
                    {validation.warnings.map((warning, index) => (
                      <li key={index}>{warning}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </section>

        {/* Download & Backups */}
        <section className="admin-section">
          <h2>💾 Data Management</h2>
          
          <div className="admin-actions">
            <button 
              onClick={handleDownload}
              disabled={loading}
              className="btn-download"
            >
              📥 Download Current Data
            </button>
          </div>

          {backups.length > 0 && (
            <div className="backups-section">
              <h3>📦 Available Backups</h3>
              <div className="backups-list">
                {backups.slice(0, 10).map((backup, index) => (
                  <div key={index} className="backup-item">
                    <div className="backup-info">
                      <div className="backup-name">{backup.filename}</div>
                      <div className="backup-meta">
                        {formatDate(backup.created)} • {formatFileSize(backup.size)}
                      </div>
                    </div>
                  </div>
                ))}
                {backups.length > 10 && (
                  <div className="backup-item more-backups">
                    ... and {backups.length - 10} more backups
                  </div>
                )}
              </div>
            </div>
          )}
        </section>
          </>
        )}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && (
          <div className="analytics-wrapper">
            <Analytics />
          </div>
        )}

        {/* Solution Requests Tab */}
        {activeTab === 'requests' && (
          <>
            <section className="admin-section">
              <h2>🚀 Solution Requests Management</h2>
              
              {/* Request Stats */}
              <div className="request-stats-grid">
                <div className="request-stat-card">
                  <h3>Total Requests</h3>
                  <div className="stat-number">{solutionRequests.length}</div>
                </div>
                <div className="request-stat-card">
                  <h3>Pending</h3>
                  <div className="stat-number pending">
                    {solutionRequests.filter(req => req.status === 'pending').length}
                  </div>
                </div>
                <div className="request-stat-card">
                  <h3>In Progress</h3>
                  <div className="stat-number in-progress">
                    {solutionRequests.filter(req => req.status === 'in-progress').length}
                  </div>
                </div>
                <div className="request-stat-card">
                  <h3>Completed</h3>
                  <div className="stat-number completed">
                    {solutionRequests.filter(req => req.status === 'completed').length}
                  </div>
                </div>
              </div>

              {/* Requests List */}
              <div className="requests-list">
                <div className="requests-header">
                  <h3>All Solution Requests</h3>
                  <button 
                    className="btn-secondary"
                    onClick={loadSolutionRequests}
                  >
                    🔄 Refresh
                  </button>
                </div>
                
                {solutionRequests.length === 0 ? (
                  <div className="no-requests">
                    <div className="no-requests-icon">📝</div>
                    <h3>No Solution Requests Yet</h3>
                    <p>When users submit new solution requests from the dashboard, they will appear here.</p>
                  </div>
                ) : (
                  <div className="requests-table">
                    {solutionRequests.map((req) => (
                      <div key={req.id} className="request-card">
                        <div className="request-header">
                          <div className="request-title-section">
                            <h4 className="request-title">{req.title}</h4>
                            <div className="request-meta">
                              <span className="request-department">
                                {req.customDepartment || req.department}
                              </span>
                              <span className="request-date">
                                {formatDate(req.submittedAt)}
                              </span>
                            </div>
                          </div>
                          <div className="request-status-section">
                            <select 
                              value={req.status} 
                              onChange={(e) => updateRequestStatus(req.id, e.target.value)}
                              className={`status-select status-${req.status}`}
                            >
                              <option value="pending">🟡 Pending</option>
                              <option value="in-progress">🔄 In Progress</option>
                              <option value="completed">✅ Completed</option>
                              <option value="cancelled">❌ Cancelled</option>
                            </select>
                            <div className={`type-badge type-${req.type || 'request'}`}>
                              {req.type === 'suggestion' ? '💡 Suggestion' : '🚀 Request'}
                            </div>
                            {req.priority && req.type !== 'suggestion' && (
                              <div className={`priority-badge priority-${req.priority}`}>
                                {getPriorityIcon(req.priority)} {req.priority}
                              </div>
                            )}
                          </div>
                        </div>
                        
                        <div className="request-content">
                          <div className="request-description">
                            <p>{req.description}</p>
                          </div>
                          
                          <div className="request-contact">
                            <strong>Contact:</strong> {req.contactName} ({req.contactEmail})
                          </div>
                          
                          {req.notes && (
                            <div className="request-notes">
                              <strong>Admin Notes:</strong> {req.notes}
                            </div>
                          )}
                        </div>
                        
                        <div className="request-actions">
                          <button 
                            className="btn-small btn-outline"
                            onClick={() => editRequestNotes(req)}
                          >
                            📝 Edit Notes
                          </button>
                          <button 
                            className="btn-small btn-outline"
                            onClick={() => deleteRequest(req.id)}
                          >
                            🗑️ Delete
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </section>
          </>
        )}

      </div>

      {loading && (
        <div className="loading-overlay">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <div>Processing...</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;